import React, { useState, useEffect, useCallback } from 'react'
import { Box, Button, Grid, Alert, Typography, Paper } from '@mui/material'
import { isEqual } from 'lodash'
import { useGlobalNavigationBlock } from '../useGlobalNavigationBlock'
import GenericSelectField from 'components/UserFields/GenericSelectField'
import SpecialtyField from 'components/UserFields/SpecialtyField'
import SubspecialtyField from 'components/UserFields/SubspecialtyField'
import GenericDatePicker from 'components/UserFields/GenericDatePicker'
import NPINumberField from 'components/UserFields/NPINumberField'
import GenericMultipleSelect from 'components/UserFields/GenericMultipleSelect'
import GenericRadioGroup from 'components/UserFields/GenericRadioGroup'
import {
  getCandidateTypeOptions,
  getBoardingEligibilityOptions,
  getStatesOptions,
  getYearsExperienceOptions,
  getAvailabilityOptions,
} from 'components/UserFields/helpers'
import { professionFields } from 'components/UserFields/professionDefinitions.js'
import { SEARCH_STATUS_HELPER_TEXT } from 'data/fieldHelperText'

const ProfessionalDetailsSection = ({ formData, onSave, candidateTypes = [] }) => {
  const [localFormData, setLocalFormData] = useState(formData)
  const [initialData, setInitialData] = useState(formData)
  const [candidateType, setCandidateType] = useState({})
  const [errors, setErrors] = useState({})
  const [hasChanges, setHasChanges] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  useGlobalNavigationBlock(hasChanges)

  // Set initial data and candidate type on mount or formData update
  useEffect(() => {
    setLocalFormData(formData)
    setInitialData(formData)
    setHasChanges(false)
    setShowWarning(false)
  }, [formData])

  useEffect(() => {
    if (localFormData.candidateTypeId) {
      const typeData = candidateTypes.find(type => type.id === localFormData.candidateTypeId)
      setCandidateType(professionFields.find(field => field.name === typeData?.name) || {})
    }
  }, [formData.candidateTypeId, localFormData.candidateTypeId, candidateTypes])

  // Clear specific field errors when user inputs values
  const clearError = fieldName => {
    if (errors[fieldName]) {
      setErrors(prevErrors => {
        // eslint-disable-next-line no-unused-vars
        const { [fieldName]: _, ...updatedErrors } = prevErrors
        return updatedErrors
      })
    }
  }

  const handleInputChange = event => {
    const { name, value } = event.target
    clearError(name)
    const updatedFormData = {
      ...localFormData,
      [name]: value,
      ...(name === 'candidateTypeId' && { specialtyIds: [], subspecialtyIds: [] }),
      ...(name === 'specialtyIds' && { subspecialtyIds: [] }),
    }
    setLocalFormData(updatedFormData)
    setHasChanges(!isEqual(updatedFormData, initialData))
    if (name === 'candidateTypeId' && value !== initialData.candidateTypeId) {
      setShowWarning(true)
    }
  }

  const handleComplexChange = (name, valueOrEvent) => {
    const value = Array.isArray(valueOrEvent) ? valueOrEvent : valueOrEvent.target?.value
    clearError(name)
    const updatedFormData = { ...localFormData, [name]: value }
    setLocalFormData(updatedFormData)
    setHasChanges(!isEqual(updatedFormData, initialData))
  }

  const validateFields = useCallback(() => {
    const newErrors = {}
    if (!localFormData.candidateTypeId) newErrors.candidateTypeId = 'Profession is required'
    if (candidateType?.specialtyIds?.required && !localFormData.specialtyIds?.length) {
      newErrors.specialtyIds = 'Specialty is required'
    }
    if (candidateType?.npiNumber?.required && !localFormData.npiNumber) {
      newErrors.npiNumber = 'NPI Number is required'
    }
    if (candidateType?.boardEligibility?.required && !localFormData.boardEligibility) {
      newErrors.boardEligibility = 'Board Eligibility is required'
    }
    if (!localFormData.yearsExperience)
      newErrors.yearsExperience = 'Years of Experience is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [localFormData, candidateType])

  const handleSave = () => {
    if (!validateFields()) return

    if (hasChanges) {
      const changedFields = Object.keys(localFormData).reduce((acc, key) => {
        if (!isEqual(localFormData[key], initialData[key])) {
          acc[key] = localFormData[key]
        }
        return acc
      }, {})

      if (Array.isArray(changedFields.specialtyIds)) {
        changedFields.specialtyIds = changedFields.specialtyIds.map(specialty =>
          parseInt(specialty.id, 10),
        )
      }
      if (Array.isArray(changedFields.subspecialtyIds)) {
        changedFields.subspecialtyIds = changedFields.subspecialtyIds.map(subspecialty =>
          parseInt(subspecialty.id, 10),
        )
      }

      onSave({ ...changedFields, id: formData.id })
      setInitialData(localFormData)
      setHasChanges(false)
      setShowWarning(false)
    }
  }

  return (
    <Box>
      {hasChanges && (
        <Alert 
          severity='info' 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'info.main'
            }
          }}
        >
          You have unsaved changes in this section. Please save before navigating away.
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Single consolidated professional details section */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default'
            }}
          >
            {/* Job Search Status */}
            <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
              Job Search Status
            </Typography>
            <Box sx={{ mb: 4 }}>
              <GenericRadioGroup
                value={localFormData.searchStatus}
                name='searchStatus'
                label=''
                toolTipText={SEARCH_STATUS_HELPER_TEXT}
                handleChange={handleInputChange}
                options={getAvailabilityOptions()}
                row={true}
                required={true}
                error={errors.searchStatus}
                helperText={errors.searchStatus && 'Please select your job search stage'}
                sx={{
                  '& .MuiFormControlLabel-root': {
                    mr: 4
                  }
                }}
              />
            </Box>

            {/* Availability */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Availability
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <GenericDatePicker
                  value={formData.availabilityDate}
                  handleChange={handleInputChange}
                  label='Availability Date'
                  name='availabilityDate'
                  required={true}
                  error={errors.availabilityDate}
                  helperText={errors.availabilityDate && 'Availability date is required'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Professional Information */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Professional Information
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <GenericSelectField
                  value={localFormData.candidateTypeId}
                  label='Profession'
                  name='candidateTypeId'
                  handleChange={handleInputChange}
                  options={getCandidateTypeOptions({ candidateTypes }) || []}
                  required
                  error={errors.candidateTypeId}
                  helperText={errors.candidateTypeId}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <SpecialtyField
                  value={localFormData.specialtyIds || []}
                  handleChange={value => handleComplexChange('specialtyIds', value)}
                  candidateTypeId={localFormData.candidateTypeId}
                  setErrors={setErrors}
                  errors={errors}
                  required={candidateType?.specialtyIds?.required}
                  name='specialtyIds'
                  label='Specialty'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <SubspecialtyField
                  value={localFormData.subspecialtyIds || []}
                  handleChange={value => handleComplexChange('subspecialtyIds', value)}
                  name='subspecialtyIds'
                  specialtyIds={localFormData.specialtyIds}
                  label='Subspecialty'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Additional Information */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Additional Information
            </Typography>
            <Grid container spacing={3}>
              {candidateType?.npiNumber?.show !== false && (
                <Grid item xs={12} md={6}>
                  <NPINumberField
                    handleChange={handleInputChange}
                    value={localFormData.npiNumber}
                    label='NPI Number'
                    name='npiNumber'
                    setErrors={setErrors}
                    errors={errors}
                    required={candidateType?.npiNumber?.required}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }
                    }}
                  />
                </Grid>
              )}
              {candidateType?.boardEligibility?.show !== false && (
                <Grid item xs={12} md={6}>
                  <GenericSelectField
                    value={localFormData.boardEligibility}
                    label='Board Eligibility'
                    name='boardEligibility'
                    handleChange={handleInputChange}
                    options={getBoardingEligibilityOptions()}
                    required={candidateType?.boardEligibility?.required}
                    error={errors.boardEligibility}
                    helperText={errors.boardEligibility}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }
                    }}
                  />
                </Grid>
              )}
              {candidateType?.licensedStates?.show !== false && (
                <Grid item xs={12}>
                  <GenericMultipleSelect
                    value={localFormData.licensedStates}
                    label='Licensed States'
                    name='licensedStates'
                    handleChange={value => handleComplexChange('licensedStates', value)}
                    options={getStatesOptions()}
                    required={candidateType?.licensedStates?.required}
                    error={errors.licensedStates}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <GenericRadioGroup
                  value={localFormData.yearsExperience}
                  label='Years of Experience'
                  name='yearsExperience'
                  handleChange={handleInputChange}
                  options={getYearsExperienceOptions()}
                  required
                  error={errors.yearsExperience}
                  sx={{
                    '& .MuiFormControlLabel-root': {
                      mr: 4
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {showWarning && (
        <Alert 
          severity='warning' 
          sx={{ 
            mt: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'warning.main'
            }
          }}
        >
          If you save these changes, your profession will be updated. After saving, please review
          your alerts and saved filters to ensure they are still relevant for your new profession.
        </Alert>
      )}

      <Button
        variant='contained'
        color='primary'
        onClick={handleSave}
        disabled={!hasChanges || Object.keys(errors).length > 0}
        sx={{ 
          mt: 4,
          px: 4,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.9375rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        }}
      >
        Save Changes
      </Button>
    </Box>
  )
}

export default ProfessionalDetailsSection
